import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@project/ui";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { getDashboardOrigin } from "@apps/dashboard/lib/env";
import { BrandWordmark } from "@apps/dashboard/components/brand/BrandWordmark";
import { AuthCenterLayout } from "@apps/dashboard/components/auth/AuthCenterLayout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

export default function ForgotPasswordPage(): JSX.Element {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const redirectTo = `${getDashboardOrigin()}${dashboardPaths.resetPassword}`;
      const { error: err } = await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo,
      });
      if (err) {
        setError(translateAuthError(err, t));
        return;
      }
      setDone(true);
    } catch (unknownErr) {
      setError(translateAuthError(unknownErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCenterLayout>
      <div className="relative z-10 w-full max-w-[480px]">
        <div className="mb-8 flex items-center gap-3">
          <Link
            to={dashboardPaths.login}
            className="rounded-full p-2 text-primary transition-colors hover:bg-surface-container-low"
            aria-label={t("common.back")}
          >
            <Icon name="ArrowLeft" className="size-6" strokeWidth={1.5} />
          </Link>
          <BrandWordmark markClassName="size-8" textClassName="text-xl font-black tracking-tight" />
        </div>

        <div className="rounded-xl border border-outline-variant/15 bg-surface-container-lowest p-10 shadow-sm">
          <h2 className="mb-2 font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.auth.forgotTitle")}</h2>
          <p className="mb-8 text-body-md text-on-surface-variant">{t("dashboardUi.auth.forgotSubtitle")}</p>

          {done ? (
            <p className="rounded-lg bg-secondary-container/20 px-4 py-4 text-body-md text-on-secondary-container" role="status">
              {t("dashboardUi.auth.forgotSentHint")}
            </p>
          ) : (
            <>
              {error ? (
                <div className="mb-6 rounded-lg bg-error/10 px-4 py-3 text-body-md text-error outline outline-1 outline-error/20" role="alert">
                  {error}
                </div>
              ) : null}
              <form className="space-y-6" onSubmit={onSubmit}>
                <div className="space-y-1.5">
                  <label htmlFor="forgot-email" className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    {t("dashboardUi.auth.emailLabel")}
                  </label>
                  <input
                    id="forgot-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    placeholder={t("dashboardUi.auth.emailPlaceholder")}
                    className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="primary-gradient h-12 w-full rounded-lg font-headline font-semibold text-on-primary shadow-md disabled:opacity-60"
                >
                  {submitting ? t("common.loading") : t("dashboardUi.auth.sendResetLink")}
                </button>
              </form>
            </>
          )}

          <p className="mt-8 text-center">
            <Link to={dashboardPaths.login} className="text-body-md font-semibold text-primary hover:underline">
              {t("dashboardUi.auth.backToLogin")}
            </Link>
          </p>
        </div>
      </div>
    </AuthCenterLayout>
  );
}
